import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { formatFieldLabel } from '@/utils/fieldLabels';
import { useOrderSizes } from '@/hooks/useOrderSizes';
import { orderSizeOptions } from '@/utils/orderSizeOptions';
import SizeChoicesUnavailable from '@/components/SizeChoicesUnavailable';
import { useOrderReview } from '@/hooks/useOrderReview';
import AuthenticatedDocumentFrame from '@/components/AuthenticatedDocumentFrame';
import AddressConfirmationDialog from '@/components/AddressConfirmationDialog';
import { API } from '@/config/constants';

// Fields the Review panel can render, and how each edits. Anything not here
// (status, internal #, sender, geocode…) intentionally lives only in the full
// Edit dialog — Review is a focused "verify what's likely wrong" checklist.
const FIELD_REGISTRY = {
  recipient_name: { type: 'text' },
  recipient_street_address: { type: 'text' },
  recipient_street_address_2: { type: 'text' },
  recipient_city: { type: 'text' },
  recipient_state: { type: 'text' },
  recipient_zip_code: { type: 'text' },
  recipient_phone: { type: 'text' },
  network_source: { type: 'text' },
  subsource_extracted: { type: 'text' },
  order_number: { type: 'text' },
  delivery_date: { type: 'date' },
  delivery_time_requirement: { type: 'text' },
  product_code: { type: 'text' },
  size: { type: 'select' },   // options come from useOrderSizes (backend KNOWN_SIZES)
  product_name: { type: 'text' },
  total_price: { type: 'number' },
  card_message: { type: 'textarea' },
  product_description: { type: 'textarea' },
  special_instructions: { type: 'textarea' },
};

// Free-text / reference fields that are perennially uncertain — always shown for
// a look, even when the AI didn't flag them (they're excluded from the flag by
// design, precisely because they'd fire on nearly every order).
const ALWAYS_REVIEW = ['card_message', 'product_description', 'order_number', 'subsource_extracted', 'product_code', 'size', 'product_name', 'recipient_street_address', 'recipient_street_address_2'];

/* ── A focused, source-beside-fields intake review (desktop/wide only). ────────
   Left: the original document, embedded + natively zoomable. Right: a short
   checklist of the fields worth verifying — this order's flagged fields plus the
   always-review set — editable and saved via the same PUT the dialog uses. */
const ReviewOpening = ({ order: incomingOrder, openingId, onClose, onSaved }) => {
  // A parent list refresh must not replace the draft or its field selection.
  const [order] = useState(incomingOrder);
  const activeSizes = useOrderSizes();

  // The set the AI was unsure about on THIS order (full set, no exclusions).
  const flaggedSet = useMemo(
    () => new Set((order?.low_confidence_fields_all || []).filter((f) => FIELD_REGISTRY[f])),
    [order]
  );

  // Show = this order's flagged fields (consequential first) + the always-review
  // block, deduped. branding_seen drops out automatically (not in the registry).
  const { flaggedKeys, alwaysKeys } = useMemo(() => {
    const flagged = [...flaggedSet].filter((f) => !ALWAYS_REVIEW.includes(f));
    const always = ALWAYS_REVIEW.filter((f) => FIELD_REGISTRY[f]);
    return { flaggedKeys: flagged, alwaysKeys: always };
  }, [flaggedSet]);

  const fieldKeys = useMemo(() => [...new Set([...flaggedKeys, ...alwaysKeys])], [flaggedKeys, alwaysKeys]);
  const workflow = useOrderReview({ order, fieldKeys, openingId, onClose, onSaved });
  const { formData, reviewed, setField, setReviewed, disabled, pending } = workflow;
  const sizes = orderSizeOptions(orderSizeOptions(activeSizes, order.size), formData.size);
  if (!workflow.visible) return null;

  const documentUrl = `${API}/orders/${order.id}/document`;

  const renderField = (key) => {
    const { type } = FIELD_REGISTRY[key];
    const flagged = flaggedSet.has(key);
    return (
      <div key={key} data-testid={`review-field-${key}`}>
        <Label className="mb-1 flex items-center gap-1 text-[12px] font-medium text-stone-600">
          {formatFieldLabel(key)}
          {flagged && (
            <AlertTriangle className="h-3 w-3 text-amber-500" strokeWidth={2.5} title="AI agreement needs review — check against the source" />
          )}
        </Label>
        {type === 'textarea' ? (
          <Textarea
            aria-label={formatFieldLabel(key)}
            disabled={disabled}
            value={formData[key] ?? ''}
            onChange={(e) => setField(key, e.target.value)}
            rows={key === 'card_message' ? 4 : 2}
            className="resize-none text-sm"
          />
        ) : type === 'select' ? (
          <Select
            disabled={disabled || !activeSizes.length}
            value={formData[key] || '__none__'}
            onValueChange={(v) => setField(key, v === '__none__' ? '' : v)}
          >
            <SelectTrigger aria-label={formatFieldLabel(key)} className="text-sm"><SelectValue placeholder="None" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">None</SelectItem>
              {sizes.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : (
          <Input
            aria-label={formatFieldLabel(key)}
            disabled={disabled}
            type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
            step={type === 'number' ? '0.01' : undefined}
            value={formData[key] ?? ''}
            onChange={(e) => setField(key, e.target.value)}
            className="text-sm"
          />
        )}
        {key === 'size' && !activeSizes.length && <SizeChoicesUnavailable />}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white" data-testid="order-review-view">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 border-b border-stone-200 px-5 py-3">
        <div className="flex items-baseline gap-3 min-w-0">
          <h2 className="truncate text-lg font-semibold text-stone-900">
            {order.recipient_name || 'Review order'}
          </h2>
          {order.internal_order_number && (
            <span className="shrink-0 font-mono text-xs text-stone-400">{order.internal_order_number}</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-stone-600">
            <Checkbox disabled={disabled} checked={reviewed} onCheckedChange={(v) => setReviewed(v === true)} data-testid="review-reviewed-checkbox" />
            Reviewed
            {reviewed && order.reviewed_by && (
              <CheckCircle2 className="h-4 w-4 text-emerald-600" title={`Reviewed by ${order.reviewed_by}`} />
            )}
          </label>
          <Button variant="outline" size="sm" onClick={workflow.requestClose} aria-label="Close review" data-testid="review-close-btn">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Split body — PDF-weighted (~65/35), fluid */}
      <div className="flex min-h-0 flex-1">
        {/* Source */}
        <div className="min-w-0 basis-[65%] border-r border-stone-200 bg-stone-100">
          <AuthenticatedDocumentFrame
            url={documentUrl}
            documentKey={JSON.stringify([order.id, order.document_id, order.resolved_source || order.network_source, order.desktop_sync?.origin, openingId])}
            orderNumber={order.internal_order_number || order.order_number}
            title="Source document"
            className="h-full w-full border-0"
            data-testid="review-source-frame"
          />
        </div>

        {/* Field checklist */}
        <div className="min-w-0 basis-[35%] overflow-y-auto p-5">
          <p className="mb-4 text-xs text-stone-500" data-testid="review-confidence-explanation">
            Confidence reflects agreement between AI answers before application rules change values.
            It does not independently verify the final values — compare them with the source document.
          </p>
          {workflow.message && (
            <div role="status" className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm" data-testid="review-status">
              <p>{workflow.message}</p>
              {workflow.refreshFailed && (
                <Button variant="outline" size="sm" className="mt-2" disabled={!!pending} onClick={workflow.retryRefresh} data-testid="review-retry-refresh">
                  Retry refresh only
                </Button>
              )}
              {workflow.unknown && (
                <Button variant="outline" size="sm" className="mt-2" disabled={!!pending} onClick={workflow.checkSavedRecord} data-testid="review-check-saved">
                  Check saved record
                </Button>
              )}
            </div>
          )}
          {workflow.unknown && workflow.evidence && (
            <div className="mb-4 rounded-lg border border-stone-200 p-3 text-sm" data-testid="review-saved-evidence">
              <h3 className="font-semibold">Compare your corrections with the saved record</h3>
              <p className="my-2 text-stone-600">This read does not prove the earlier save stopped. Your corrections have not been replaced.</p>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead><tr><th className="p-1">Field</th><th className="p-1">Your correction</th><th className="p-1">Saved now</th></tr></thead>
                  <tbody>
                    {fieldKeys.map(key => (
                      <tr key={key} className="border-t border-stone-100">
                        <th className="p-1 font-normal">{formatFieldLabel(key)}</th>
                        <td className="p-1 whitespace-pre-wrap break-words">{String(formData[key] ?? '') || '—'}</td>
                        <td className="p-1 whitespace-pre-wrap break-words">{String(workflow.evidence[key] ?? '') || '—'}</td>
                      </tr>
                    ))}
                    <tr className="border-t border-stone-100"><th className="p-1 font-normal">Reviewed</th><td className="p-1">{reviewed ? 'Yes' : 'No'}</td><td className="p-1">{workflow.evidence.reviewed_by ? 'Yes' : 'No'}</td></tr>
                  </tbody>
                </table>
              </div>
              <Button variant="outline" size="sm" className="mt-3" disabled={!!pending} onClick={workflow.allowRetry} data-testid="review-allow-retry">
                I reviewed the saved record — return to editing
              </Button>
            </div>
          )}
          <div className="flex flex-col gap-4">
            {/* Order identity + source — anchor row on top */}
            <div className="grid grid-cols-2 gap-3">
              {renderField('order_number')}
              {renderField('subsource_extracted')}
            </div>

            {/* Recipient address — line 1 + line 2 */}
            <div className="grid grid-cols-2 gap-3">
              {renderField('recipient_street_address')}
              {renderField('recipient_street_address_2')}
            </div>

            {/* Fields the AI flagged on THIS order */}
            {flaggedKeys.length > 0 && (
              <>
                <div className="border-t border-stone-100" />
                <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-600">Flagged on this order</p>
                {flaggedKeys.map(renderField)}
              </>
            )}

            {/* Always double-check */}
            <div className="border-t border-stone-100" />
            <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">Always double-check</p>
            {renderField('card_message')}
            {/* Product: description on the left; code / size / name stacked on the
                right. The right stack sets the height; the description textarea
                stretches to match it (no fixed size on either). */}
            <div className="grid grid-cols-2 items-stretch gap-3">
              <div className="flex flex-col" data-testid="review-field-product_description">
                <Label className="mb-1 flex items-center gap-1 text-[12px] font-medium text-stone-600">
                  {formatFieldLabel('product_description')}
                  {flaggedSet.has('product_description') && (
                    <AlertTriangle className="h-3 w-3 text-amber-500" strokeWidth={2.5} title="The AI wasn't fully sure — check against the source" />
                  )}
                </Label>
                <Textarea
                  aria-label={formatFieldLabel('product_description')}
                  disabled={disabled}
                  value={formData.product_description ?? ''}
                  onChange={(e) => setField('product_description', e.target.value)}
                  className="flex-1 resize-none text-sm"
                />
              </div>
              <div className="flex flex-col gap-3">
                {renderField('product_code')}
                {renderField('size')}
                {renderField('product_name')}
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-end gap-2 border-t border-stone-200 pt-4">
            <Button variant="outline" onClick={workflow.requestClose} data-testid="review-cancel-btn">Cancel</Button>
            <Button onClick={workflow.save} disabled={!workflow.canSave} data-testid="review-save-btn">
              {pending || 'Save'}
            </Button>
          </div>
        </div>
      </div>
      {workflow.addressConfirmation && (
        <AddressConfirmationDialog {...workflow.addressReview} difference={workflow.addressConfirmation} onAccept={workflow.acceptAddress} onEdit={workflow.editAddress} busy={!!pending} />
      )}
    </div>
  );
};

export const OrderReviewView = ({ order, openingId, ...props }) => order ? (
  <ReviewOpening key={JSON.stringify([order.id, openingId ?? null])} order={order} openingId={openingId} {...props} />
) : null;

export default OrderReviewView;
